import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import {
  UserPlus, LogIn, LogOut, ExternalLink, Clock, CheckCircle, AlertTriangle,
  Send, Save, Lock, FileText, Ruler, Film, GraduationCap, Mail, Shield,
} from 'lucide-react';
import { authFetch, API } from '../api';
import { POSITION_GROUPS } from '../positions';

/*
 * Guardian-facing shell.
 *
 * Deliberately separate from the coach application: a parent should never see
 * Players, Criteria or the Recruit Board. This mounts on /athlete/* and shares
 * nothing with the coach routes except the design system and the API.
 */

const input =
  'w-full bg-[#243B55] text-white px-3 py-2.5 rounded-lg border border-[#546E7A]/30 ' +
  'focus:border-[#00D4FF] focus:outline-none transition-colors text-sm';

const Field = ({ label, hint, locked, children }) => (
  <div>
    <label className="flex items-center gap-1.5 text-[#B0BEC5] text-sm mb-1.5">
      {label}
      {locked && <Lock size={11} className="text-[#FFB300]" />}
    </label>
    {children}
    {hint && <div className="text-[#546E7A] text-xs mt-1">{hint}</div>}
  </div>
);

const Card = ({ icon: Icon, title, subtitle, children }) => (
  <div className="panel-card mb-4">
    <div className="flex items-start gap-2.5 mb-4">
      <Icon size={18} className="text-[#00D4FF] flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <h3 className="text-white font-bold">{title}</h3>
        {subtitle && <p className="text-[#546E7A] text-xs mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA',
  'ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK',
  'OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

const STATUS = {
  draft: { label: 'Draft', color: '#546E7A', help: 'Not submitted yet. Only you can see this.' },
  submitted: { label: 'Submitted for review', color: '#FFB300', help: 'We are reviewing it and will send an invoice.' },
  changes_requested: { label: 'Changes requested', color: '#EF5350', help: 'See the note below, then resubmit.' },
  live: { label: 'Live', color: '#00E676', help: 'Your page is public and shareable.' },
  expired: { label: 'Expired', color: '#546E7A', help: 'The page is offline. Contact us to renew.' },
  cancelled: { label: 'Cancelled', color: '#546E7A', help: 'This profile has been cancelled.' },
};

/* ── auth ─────────────────────────────────────────────────────────────── */

const AthleteAuth = ({ onAuthed }) => {
  const [mode, setMode] = useState('register');
  const [busy, setBusy] = useState(false);
  const [schoolHits, setSchoolHits] = useState([]);
  const [schoolChoices, setSchoolChoices] = useState([]);
  const [otherSchool, setOtherSchool] = useState(false);
  const [error, setError] = useState('');
  const [f, setF] = useState({
    email: '', password: '', full_name: '', athlete_name: '',
    position: '', high_school: '', hs_city: '', hs_state: '',
    graduation_year: '', plan: 'annual',
  });

  // City and state come first, then the schools there are listed. A parent
  // recognizes their own school in a short list far more reliably than they
  // spell it, and picking from the list is what prevents a near-duplicate.
  useEffect(() => {
    if (!f.hs_state || f.hs_city.trim().length < 2) { setSchoolChoices([]); return; }
    const timer = setTimeout(async () => {
      try {
        const qs = new URLSearchParams({ state: f.hs_state, city: f.hs_city.trim() });
        const r = await fetch(`${API}/api/athlete/schools?${qs}`);
        if (r.ok) setSchoolChoices(await r.json());
      } catch { setSchoolChoices([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [f.hs_state, f.hs_city]);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const url = mode === 'register'
        ? `${API}/api/athlete/register`
        : `${API}/api/auth/login`;
      const body = mode === 'register'
        ? { ...f, graduation_year: f.graduation_year ? parseInt(f.graduation_year, 10) : null }
        : { email: f.email, password: f.password };
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Something went wrong');
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onAuthed(data.user);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center px-4 py-8">
      <div className="max-w-lg w-full">
        <div className="text-center mb-6">
          <img src="/SchemeSync_Logo.png" alt="" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl md:text-3xl font-bold text-white">
            <span className="text-[#00D4FF]">Scheme</span>Sync
            <span className="text-[#00E676] ml-1.5 text-lg">HS</span>
          </h1>
          <p className="text-[#B0BEC5] text-sm mt-2">
            Build your athlete's recruiting page.
          </p>
        </div>

        <form onSubmit={submit} className="panel-card space-y-3.5">
          <div className="flex gap-2 mb-2">
            {[['register', 'Create account'], ['login', 'Sign in']].map(([m, label]) => (
              <button key={m} type="button" onClick={() => { setMode(m); setError(''); }}
                className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold border transition-colors ${
                  mode === m ? 'bg-[#00D4FF]/15 border-[#00D4FF]/50 text-[#00D4FF]'
                             : 'bg-transparent border-[#546E7A]/30 text-[#546E7A]'}`}>
                {label}
              </button>
            ))}
          </div>

          {mode === 'register' && (
            <>
              <Field label="Your name (parent or guardian)">
                <input required className={input} value={f.full_name}
                  onChange={(e) => setF({ ...f, full_name: e.target.value })} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <Field label="Athlete's full name">
                  <input required className={input} value={f.athlete_name}
                    onChange={(e) => setF({ ...f, athlete_name: e.target.value })} />
                </Field>
                <Field label="Position"
                  hint="Their main position. It decides which measurables we ask for.">
                  <select required className={input} value={f.position}
                    onChange={(e) => setF({ ...f, position: e.target.value })}>
                    <option value="">Choose a position…</option>
                    {POSITION_GROUPS.map((g) => (
                      <optgroup key={g.group} label={g.group}>
                        {g.positions.map((p) => (
                          <option key={p.code} value={p.code}>{p.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3.5">
                <Field label="School city">
                  <input required className={input} value={f.hs_city}
                    onChange={(e) => { setOtherSchool(false); setF({ ...f, hs_city: e.target.value, high_school: '' }); }} />
                </Field>
                <Field label="State">
                  <select required className={input} value={f.hs_state}
                    onChange={(e) => { setOtherSchool(false); setF({ ...f, hs_state: e.target.value, high_school: '' }); }}>
                    <option value="">—</option>
                    {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                </Field>
                <Field label="Grad year">
                  <input className={input} type="number" inputMode="numeric" value={f.graduation_year}
                    onChange={(e) => setF({ ...f, graduation_year: e.target.value })} />
                </Field>
              </div>

              <Field label="High school"
                hint={f.hs_state && f.hs_city.trim().length >= 2
                  ? 'Pick your school so your athlete lands with the right program.'
                  : 'Enter the city and state above and your school will appear here.'}>
                {!otherSchool ? (
                  <select required className={input} value={f.high_school}
                    disabled={!f.hs_state || f.hs_city.trim().length < 2}
                    onChange={(e) => {
                      if (e.target.value === '__other__') { setOtherSchool(true); setF({ ...f, high_school: '' }); }
                      else setF({ ...f, high_school: e.target.value });
                    }}>
                    <option value="">
                      {schoolChoices.length ? 'Choose your school…' : 'No schools listed yet — choose Other'}
                    </option>
                    {schoolChoices.map((h) => (
                      <option key={h.key} value={h.name}>{h.name}</option>
                    ))}
                    <option value="__other__">Other — my school isn&rsquo;t listed</option>
                  </select>
                ) : (
                  <div className="flex gap-2">
                    <input required className={input} value={f.high_school} autoFocus
                      placeholder="Type your school's full name"
                      onChange={(e) => setF({ ...f, high_school: e.target.value })} />
                    <button type="button"
                      onClick={() => { setOtherSchool(false); setF({ ...f, high_school: '' }); }}
                      className="text-xs font-semibold text-[#546E7A] border border-[#546E7A]/40 rounded-lg px-3 flex-shrink-0">
                      Back
                    </button>
                  </div>
                )}
              </Field>
              <Field label="Plan" hint="You will be invoiced after you submit. Nothing is charged now.">
                <select className={input} value={f.plan}
                  onChange={(e) => setF({ ...f, plan: e.target.value })}>
                  <option value="annual">Annual</option>
                  <option value="monthly">Monthly</option>
                </select>
              </Field>
            </>
          )}

          <Field label="Email">
            <input required type="email" className={input} value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })} />
          </Field>
          <Field label="Password" hint={mode === 'register' ? 'At least 8 characters.' : null}>
            <input required type="password" className={input} value={f.password}
              onChange={(e) => setF({ ...f, password: e.target.value })} />
          </Field>

          {error && <div className="text-[#EF5350] text-sm">{error}</div>}

          <button type="submit" disabled={busy}
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50">
            {mode === 'register' ? <UserPlus size={16} /> : <LogIn size={16} />}
            {busy ? 'Working…' : mode === 'register' ? 'Create account' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AthleteAuth;
export { input, Field, Card, STATUS, US_STATES };
